import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Chip,
} from '@mui/material';
import {
  AutoAwesome,
  Add,
  Save,
  Download,
  ContentCopy,
} from '@mui/icons-material';

export const PromptGeneratorPage: React.FC = () => {
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [promptData, setPromptData] = useState([
    { question: '', category: '', custom: '' }
  ]);

  const categories = [
    'Healthcare',
    'Real Estate',
    'Finance',
    'Education',
    'Technology',
    'Retail',
    'Other'
  ];

  const handleAddRow = () => {
    setPromptData([...promptData, { question: '', category: '', custom: '' }]);
  };

  const handleUpdateRow = (index: number, field: string, value: string) => {
    const updated = [...promptData];
    updated[index] = { ...updated[index], [field]: value };
    setPromptData(updated);
  };

  const handleRemoveRow = (index: number) => {
    if (promptData.length > 1) {
      setPromptData(promptData.filter((_, i) => i !== index));
    }
  };

  const handleGeneratePrompt = () => {
    // TODO: Implement prompt generation
    const samplePrompt = `You are an expert prompt designer helping users craft detailed, effective prompts for their Voice AI agents.

Business/Company Overview: [Your business details here]
Target audience: [Your target audience]
Value Proposition: [Your value proposition]
Agent profile: [Agent characteristics]
Conversation flow: [Step-by-step conversation flow]
Additional Value Points: [Additional benefits]
Objection Handling: [How to handle objections]
Integrations & Automation Highlights: [Technical capabilities]
FAQ agent Can Answer: [Common questions]`;
    
    setGeneratedPrompt(samplePrompt);
  };

  const handleCopyPrompt = () => {
    navigator.clipboard.writeText(generatedPrompt);
  };

  const handleDownloadPrompt = () => {
    const blob = new Blob([generatedPrompt], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `prompt_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Meta Prompt Generator
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Generate AI prompts from structured data
      </Typography>

      <Grid container spacing={3}>
        {/* Template Selection */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Template Selection
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Select Template</InputLabel>
                <Select
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                  label="Select Template"
                >
                  <MenuItem value="voice-agent">Voice Agent</MenuItem>
                  <MenuItem value="customer-service">Customer Service</MenuItem>
                  <MenuItem value="sales">Sales</MenuItem>
                  <MenuItem value="support">Support</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
              </FormControl>
            </CardContent>
          </Card>
        </Grid>

        {/* Prompt Data Entry */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Prompt Data
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleAddRow}
                >
                  Add Row
                </Button>
              </Box>
              
              {promptData.map((row, index) => (
                <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Question/Prompt"
                        value={row.question}
                        onChange={(e) => handleUpdateRow(index, 'question', e.target.value)}
                        placeholder="Enter your question or prompt"
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth>
                        <InputLabel>Category</InputLabel>
                        <Select
                          value={row.category}
                          onChange={(e) => handleUpdateRow(index, 'category', e.target.value)}
                          label="Category"
                        >
                          {categories.map((category) => (
                            <MenuItem key={category} value={category}>
                              {category}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Custom (if Other)"
                        value={row.custom}
                        onChange={(e) => handleUpdateRow(index, 'custom', e.target.value)}
                        placeholder="Enter custom category"
                        disabled={row.category !== 'Other'}
                      />
                    </Grid>
                    <Grid item xs={12} md={1}>
                      <Button
                        color="error"
                        onClick={() => handleRemoveRow(index)}
                        disabled={promptData.length === 1}
                      >
                        Remove
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Generate Button */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="center">
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<AutoAwesome />}
                  onClick={handleGeneratePrompt}
                >
                  Generate Prompt
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Generated Prompt */}
        {generatedPrompt && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    Generated Prompt
                  </Typography>
                  <Box display="flex" gap={1}>
                    <Button
                      variant="outlined"
                      startIcon={<ContentCopy />}
                      onClick={handleCopyPrompt}
                    >
                      Copy
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Download />}
                      onClick={handleDownloadPrompt}
                    >
                      Download
                    </Button>
                  </Box>
                </Box>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography
                    component="pre"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      lineHeight: 1.5,
                    }}
                  >
                    {generatedPrompt}
                  </Typography>
                </Paper>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
